import type { AppType } from '@seoplum/api/app'
import { hc } from 'hono/client'

/**
 * The workspace the URL is currently addressing.
 *
 * Better Auth keeps the active organization on the session, which is shared by
 * every tab. Sending it explicitly means a request is scoped by the URL that
 * triggered it rather than by whichever tab switched workspace most recently.
 * The API re-proves membership, so this is a hint, never a grant.
 */
let activeOrganizationId: string | null = null

export function setRequestOrganization(organizationId: string | null) {
  activeOrganizationId = organizationId
}

export const api = hc<AppType>(import.meta.env.VITE_API_URL || '', {
  // Session lives in an httpOnly cookie, so every call must send credentials.
  init: { credentials: 'include' },
  headers: (): Record<string, string> =>
    activeOrganizationId ? { 'X-Organization-Id': activeOrganizationId } : {},
})

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors?: Record<string, string>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Unwraps a Hono RPC response, turning non-2xx into a typed throw. */
export async function unwrap<T>(response: Response & { json: () => Promise<unknown> }) {
  const body = (await response.json().catch(() => null)) as {
    error?: string
    fieldErrors?: Record<string, string>
  } | null

  if (!response.ok) {
    throw new ApiError(
      body?.error ?? `Request failed (${response.status})`,
      response.status,
      body?.fieldErrors,
    )
  }
  return body as T
}
